import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { scheduleService } from '../../services/schedule.service';
import { transcriptService } from '../../services/transcript.service';
import { planService } from '../../services/plan.service';
import { Schedule, Transcript, AcademicPlan } from '../../types';
import { BarChart, LineChart, ChartLegend } from '../UI/Charts';
import {
  TargetIcon,
  LayersIcon,
  CalendarIcon,
  BookIcon,
  SparkleIcon,
  ArrowUpRightIcon,
  ArrowRightIcon,
  UploadIcon,
  TrendUpIcon,
} from '../UI/Icons';

interface StatCard {
  label: string;
  value: string;
  delta?: { value: string; positive: boolean; caption: string };
  icon: React.FC<{ width?: number; height?: number; className?: string }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [plans, setPlans] = useState<AcademicPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [schedulesData, transcriptsData, plansData] = await Promise.all([
          scheduleService.getSchedules(),
          transcriptService.getTranscripts(),
          planService.getPlans(),
        ]);
        setSchedules(schedulesData);
        setTranscripts(transcriptsData);
        setPlans(plansData);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const transcript = transcripts[0];
  const terms = useMemo(() => transcript?.terms ?? [], [transcript]);

  const plannedCourses = useMemo(
    () => schedules.reduce((sum, s) => sum + (s.courses?.length ?? 0), 0),
    [schedules]
  );

  const completedCourses = useMemo(
    () => transcript?.courses_extracted?.length ?? 0,
    [transcript]
  );

  const { lastTermAvg, prevTermAvg } = useMemo(() => {
    const withGpa = terms.filter((t) => typeof t.stats?.term_gpa === 'number');
    return {
      lastTermAvg: withGpa[withGpa.length - 1]?.stats?.term_gpa,
      prevTermAvg: withGpa[withGpa.length - 2]?.stats?.term_gpa,
    };
  }, [terms]);

  const stats: StatCard[] = [
    {
      label: 'Cumulative Average',
      value: transcript?.cgpa != null ? `${transcript.cgpa}%` : '—',
      delta:
        lastTermAvg != null && prevTermAvg != null
          ? {
              value: `${lastTermAvg - prevTermAvg >= 0 ? '+' : ''}${(lastTermAvg - prevTermAvg).toFixed(1)}`,
              positive: lastTermAvg - prevTermAvg >= 0,
              caption: 'vs. last term',
            }
          : undefined,
      icon: TargetIcon,
    },
    {
      label: 'Courses Completed',
      value: completedCourses ? String(completedCourses) : '—',
      delta: terms.length
        ? { value: `${terms.length}`, positive: true, caption: 'terms on record' }
        : undefined,
      icon: BookIcon,
    },
    {
      label: 'Terms Planned',
      value: String(schedules.length),
      delta: plans.length
        ? { value: `${plans.length}`, positive: true, caption: plans.length === 1 ? 'plan' : 'plans' }
        : undefined,
      icon: CalendarIcon,
    },
    {
      label: 'Courses Planned',
      value: String(plannedCourses),
      delta:
        transcript?.major_average != null
          ? { value: `${transcript.major_average}%`, positive: true, caption: 'major average' }
          : undefined,
      icon: LayersIcon,
    },
  ];

  const courseLoadData = useMemo(
    () =>
      terms.map((t) => ({
        label: t.level || t.name.split(' ')[0].slice(0, 4),
        value: t.courses?.length ?? 0,
      })),
    [terms]
  );

  const averageSeries = useMemo(() => {
    const termPoints = terms
      .filter((t) => typeof t.stats?.term_gpa === 'number')
      .map((t) => ({ label: t.level || t.name.split(' ')[0].slice(0, 4), value: t.stats!.term_gpa! }));
    const cumulativePoints = terms
      .filter((t) => typeof t.stats?.cumulative_gpa === 'number')
      .map((t) => ({
        label: t.level || t.name.split(' ')[0].slice(0, 4),
        value: t.stats!.cumulative_gpa!,
      }));

    const series = [];
    if (termPoints.length) series.push({ label: 'Term', color: '#7c3aed', points: termPoints });
    if (cumulativePoints.length)
      series.push({ label: 'Cumulative', color: '#34d399', points: cumulativePoints });
    return series;
  }, [terms]);

  const recentCourses = useMemo(() => {
    const all = terms.flatMap((t) =>
      (t.courses ?? []).map((c) => ({ ...c, termName: t.name, level: t.level }))
    );
    return all.slice(-5).reverse();
  }, [terms]);

  const upcomingTerms = useMemo(
    () => schedules.filter((s) => s.is_planned).slice(0, 4),
    [schedules]
  );

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="h-8 w-56 animate-pulse rounded-lg bg-canvas-200" />
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl bg-canvas-200/70" />
          ))}
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl bg-canvas-200/70" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-canvas-900 sm:text-3xl">
            Welcome back, {user?.full_name?.split(' ')[0] || 'Student'}
          </h1>
          <p className="mt-1.5 text-sm text-canvas-600">
            {transcript
              ? `${transcript.degree_type ?? 'Your program'} · ${terms.length} terms on record`
              : 'Upload a transcript to unlock your full academic picture.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/transcript" className="btn-secondary">
            <UploadIcon width={16} height={16} />
            Upload transcript
          </Link>
          <Link to="/advisor" className="btn-primary">
            <SparkleIcon width={16} height={16} />
            Ask advisor
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(({ label, value, delta, icon: Icon }) => (
          <div key={label} className="card p-5">
            <div className="flex items-start justify-between">
              <span className="text-sm text-canvas-600">{label}</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                <Icon width={17} height={17} />
              </span>
            </div>
            <p className="mt-3 font-display text-3xl font-bold tracking-tight text-canvas-900">
              {value}
            </p>
            {delta && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs">
                <span className={delta.positive ? 'font-semibold text-emerald-600' : 'font-semibold text-rose-500'}>
                  {delta.value}
                </span>
                <span className="text-canvas-500">{delta.caption}</span>
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Charts + side panel */}
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-3">
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-canvas-900">Course Load</h2>
            <ChartLegend items={[{ label: 'Courses', color: '#7c3aed' }]} />
          </div>
          <div className="mt-4 h-48">
            <BarChart data={courseLoadData} />
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-canvas-900">Average by Term</h2>
            <ChartLegend
              items={[
                { label: 'Term', color: '#7c3aed' },
                { label: 'Cumulative', color: '#34d399' },
              ]}
            />
          </div>
          <div className="mt-4 h-48">
            <LineChart series={averageSeries} />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Promo / AI card */}
          <div className="relative overflow-hidden rounded-2xl bg-ink p-5 text-white">
            <div
              className="pointer-events-none absolute inset-0 opacity-80"
              style={{
                backgroundImage:
                  'radial-gradient(at 85% 15%, rgba(167,139,250,0.45) 0px, transparent 55%), radial-gradient(at 20% 90%, rgba(255,184,132,0.25) 0px, transparent 55%)',
              }}
            />
            <div className="relative">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                <SparkleIcon width={18} height={18} />
              </span>
              <h3 className="mt-3.5 font-display text-lg font-semibold leading-snug">
                Ask what to take next term
              </h3>
              <p className="mt-1.5 text-sm text-white/70">
                The advisor already knows your grades, your plan, and the live catalog.
              </p>
              <Link
                to="/advisor"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-80"
              >
                Start a conversation
                <ArrowUpRightIcon width={15} height={15} />
              </Link>
            </div>
          </div>

          {/* Upcoming terms */}
          <div className="card flex-1 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-base font-semibold text-canvas-900">Upcoming Terms</h2>
              <Link
                to="/schedule"
                className="text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
              >
                View all
              </Link>
            </div>
            {upcomingTerms.length === 0 ? (
              <p className="mt-6 text-center text-sm text-canvas-500">No terms planned yet</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {upcomingTerms.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-3 rounded-xl border border-canvas-200 px-3 py-2.5"
                  >
                    {s.term_number && (
                      <span
                        className={`chip shrink-0 ${
                          s.term_number.startsWith('WT')
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-primary-100 text-primary-700'
                        }`}
                      >
                        {s.term_number}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm text-canvas-800">
                      {s.term} {s.year}
                    </span>
                    <span className="shrink-0 text-xs text-canvas-500">
                      {s.courses?.length ?? 0} {s.courses?.length === 1 ? 'course' : 'courses'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Recent courses */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-canvas-900">Recent Courses</h2>
            <Link
              to="/transcript"
              className="text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              View transcript
            </Link>
          </div>

          {recentCourses.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-canvas-500">No transcript uploaded yet</p>
              <Link to="/transcript" className="btn-secondary mt-4">
                <UploadIcon width={16} height={16} />
                Upload transcript
              </Link>
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-canvas-200">
              {recentCourses.map((c, i) => (
                <li key={`${c.course_code}-${i}`} className="flex items-center gap-3 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-canvas-100 text-xs font-semibold text-canvas-700">
                    {c.course_code?.split(' ')[0]?.slice(0, 3)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-canvas-900">{c.course_code}</p>
                    <p className="truncate text-xs text-canvas-500">
                      {c.course_name || c.termName}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-canvas-500">{c.level || c.termName}</span>
                  {c.grade && (
                    <span
                      className={`chip shrink-0 ${
                        Number(c.grade) >= 80
                          ? 'bg-emerald-50 text-emerald-700'
                          : Number(c.grade) >= 60
                          ? 'bg-primary-50 text-primary-700'
                          : 'bg-canvas-100 text-canvas-700'
                      }`}
                    >
                      {c.grade}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Quick actions */}
        <div className="card p-5">
          <h2 className="font-display text-base font-semibold text-canvas-900">Quick Actions</h2>
          <div className="mt-3 space-y-2">
            {[
              { to: '/courses', label: 'Browse the course catalog', icon: BookIcon },
              { to: '/schedule', label: 'Build a term plan', icon: CalendarIcon },
              { to: '/transcript', label: 'Upload a transcript', icon: UploadIcon },
              { to: '/advisor', label: 'Get recommendations', icon: TrendUpIcon },
            ].map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-center gap-3 rounded-xl border border-canvas-200 px-3.5 py-3 transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-card"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                  <Icon width={16} height={16} />
                </span>
                <span className="flex-1 text-sm font-medium text-canvas-800">{label}</span>
                <ArrowRightIcon
                  width={16}
                  height={16}
                  className="text-canvas-400 transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
